import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import PowerBiEmbed from "./PowerBiEmbed";
import { useNavigate } from "react-router-dom";
import { BarChart3, Calendar, Search } from "lucide-react";

const Result = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const powerBiLinks = user?.powerBiLinks || [];
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  // Filtered Reports
  const filteredReports = useMemo(() => {
    return powerBiLinks.filter((report) =>
      report.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [powerBiLinks, searchTerm]);

  const handleRowClick = (record, index) => {
    navigate(`/powerbi-module/${index}`, { state: record });
  };

  // Create combined cards (real + dummy to make total 6)
  const visibleReports = filteredReports.slice(0, 6);
  const dummyCount = Math.max(0, 6 - visibleReports.length);

  const dummyCards = Array.from({ length: dummyCount }, (_, i) => ({
    isDummy: true,
    id: `dummy-${i}`
  }));

  const reportCards = [...visibleReports, ...dummyCards];

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8">
      <ToastContainer />
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            Power BI Dashboard Reports
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            Access your special dashboard reports
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
            />
          </div>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {reportCards.map((report, index) =>
            report.isDummy ? (
              <div
                key={report.id}
                className="bg-gray-100 rounded-lg border border-dashed border-gray-300 p-6 opacity-70 cursor-not-allowed"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-gray-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-500 mb-2 line-clamp-2">
                  Upcoming Report
                </h3>
                <p className="text-gray-500 text-sm mb-4 line-clamp-3">
                  Your special report awaits here. Kindly request access to view it.
                </p>
                <div className="text-xs text-gray-400">
                  <Calendar className="inline-block w-3 h-3 mr-1" />
                  Not yet available
                </div>
              </div>
            ) : (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {report.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {report.description}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Updated:{" "}
                    {new Date(report.updatedAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric"
                    })}
                  </div>
                </div>
                <button
                  className="buttonHeading py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors duration-200 text-sm font-medium"
                  onClick={() => handleRowClick(report, index)}
                >
                  Open Report
                </button>
              </div>
            )
          )}
        </div>

        {/* No Results */}
        {filteredReports.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No reports found
            </h3>
            <p className="text-gray-600">
              {searchTerm
                ? `No reports match "${searchTerm}"`
                : "No Power BI reports available"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Result;
